/**
 * Parser local de QR/EAN — utilisé pour résoudre INSTANTANÉMENT un scan
 * sans round-trip backend `/scan/resolve` quand le lot est déjà en mémoire.
 *
 * Reconnaît les 5 formats produits par l'app :
 *   1. URL trace : `https://maison-thai.ch/trace/{sku}/{lot}` (ou .pro etc.)
 *   2. JSON compact : `{"l":"L-2026-…","s":"PT-POU","d":"2026-07-10"}`
 *   3. Schema propriétaire : `maisonthai://lot/L-2026-…`
 *   4. Format court : `lot:L-2026-…;sku:PT-POU;dlc:…`
 *   5. Fallback : chaîne brute = lot_number tel quel
 *
 * Retourne `{ lot_number, sku }` avec `lot_number` toujours présent
 * (string non vide), `sku` optionnel. Retourne null si rien d'exploitable.
 *
 * Cette fonction NE FAIT PAS d'appel réseau. Le matching DB doit être fait
 * par l'appelant (idéalement via une Map locale `lot_number -> item`).
 */
export function parseQrLocally(raw) {
  if (!raw || typeof raw !== "string") return null;
  const code = raw.trim();
  if (!code) return null;

  // ── 1. URL trace : .../trace/{sku}/{lot} ──
  const traceMatch = code.match(/\/trace\/([^/?#\s]+)\/([^/?#\s]+)/i);
  if (traceMatch) {
    return { sku: decodeURIComponent(traceMatch[1]), lot_number: decodeURIComponent(traceMatch[2]) };
  }

  // ── 2. JSON compact ──
  if (code.startsWith("{") && code.endsWith("}")) {
    try {
      const obj = JSON.parse(code);
      const lot = obj.l || obj.lot || obj.lot_number;
      if (lot) {
        return { lot_number: String(lot), sku: obj.s || obj.sku || null };
      }
    } catch {
      /* fallthrough */
    }
  }

  // ── 3. Schema propriétaire `maisonthai://lot/…` ──
  const schemaMatch = code.match(/^maisonthai:\/\/lot\/(.+)$/i);
  if (schemaMatch) {
    return { lot_number: schemaMatch[1].trim(), sku: null };
  }

  // ── 4. Format court `lot:X;sku:Y;dlc:Z` ──
  if (code.includes("lot:")) {
    const parts = code.split(";").map((p) => p.trim());
    const fields = {};
    for (const p of parts) {
      const idx = p.indexOf(":");
      if (idx > 0) fields[p.slice(0, idx).toLowerCase()] = p.slice(idx + 1).trim();
    }
    if (fields.lot) {
      return { lot_number: fields.lot, sku: fields.sku || null };
    }
  }

  // ── 5. EAN13 brut (12-13 chiffres) : pas un lot_number, on ne résout PAS localement
  //    — l'appelant gérera ce cas spécifiquement via product.ean13_base.
  if (/^\d{12,13}$/.test(code)) {
    return null;
  }

  // ── Fallback : chaîne brute traitée comme lot_number ──
  return { lot_number: code, sku: null };
}
